import { lazy, Suspense, type ReactElement } from 'react';
import {
  type LoaderFunctionArgs,
  createBrowserRouter,
  createRoutesFromElements,
  Route
} from 'react-router';
import {
  RootErrorBoundary,
  Loader,
  RedirectWithQuery
} from '@link/share/components';
import { LOCALE_LIST, DEFAULT_LOCALE } from '@link/share/i18n';
import i18n from '@link/shop/i18n';
import { ShopLayout } from '@link/shop/layouts';
import PrivateRoute from './PrivateRoute';
import styles from './loader.module.css';

const Home = lazy(() => import('@link/shop/views/Home'));
const Product = lazy(() => import('@link/shop/views/Product'));
const BookConfirm = lazy(() => import('@link/shop/views/Book/BookConfirm'));
const BookResult = lazy(() => import('@link/shop/views/Book/BookResult'));
const Booking = lazy(() => import('@link/shop/views/Booking'));
const BookingDetail = lazy(
  () => import('@link/shop/views/Booking/BookingDetail')
);
const FAQ = lazy(() => import('@link/shop/views/FAQ'));
const Terms = lazy(() => import('@link/share/views/Terms'));
const Privacy = lazy(() => import('@link/share/views/Privacy'));
const Law = lazy(() => import('@link/shop/views/Law'));
const NotFound = lazy(() => import('@link/shop/views/NotFound'));
const Login = lazy(() => import('@link/shop/views/Login'));
const Profile = lazy(() => import('@link/shop/views/Profile'));
const ChangePassword = lazy(() => import('@link/shop/views/ChangePassword'));
const ResetPassword = lazy(() => import('@link/shop/views/ResetPassword'));
const EmailVerification = lazy(
  () => import('@link/shop/views/EmailVerification')
);
const Invoice = lazy(() => import('@link/shop/views/Invoice'));

const lazyView = (view: ReactElement) => {
  return (
    <Suspense fallback={<Loader className={styles.loader} />}>{view}</Suspense>
  );
};

const langLoader = async ({
  params
}: LoaderFunctionArgs): Promise<{ lang: string }> => {
  if (params.lang && LOCALE_LIST.includes(params.lang)) {
    return { lang: params.lang };
  }
  return { lang: DEFAULT_LOCALE };
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route path="/:lang" element={<ShopLayout />} loader={langLoader}>
        <Route index element={<RedirectWithQuery to="home" />} />
        <Route path="home" element={lazyView(<Home />)} />
        <Route path="product/:productId" element={lazyView(<Product />)} />
        <Route element={<PrivateRoute />}>
          <Route
            path="book/confirm"
            element={lazyView(<BookConfirm />)}
            handle={{ disableLang: true }}
          />
          <Route
            path="book/result/:bookingId"
            element={lazyView(<BookResult />)}
            handle={{ disableLang: true }}
          />
          <Route
            path="book/cart/result/:cartId"
            element={lazyView(<BookResult />)}
            handle={{ disableLang: true }}
          />
          <Route
            path="booking"
            element={lazyView(<Booking />)}
            handle={{ title: i18n.t('views.booking.title') }}
          />
          <Route
            path="booking/:bookingId"
            element={lazyView(<BookingDetail />)}
            handle={{ title: i18n.t('views.booking.title') }}
          />
          <Route
            path="profile"
            element={lazyView(<Profile />)}
            handle={{ title: i18n.t('share.profile.title') }}
          />
          <Route
            path="change-password"
            element={lazyView(<ChangePassword />)}
            handle={{ title: i18n.t('share.password.changePassword') }}
          />
        </Route>
        <Route path="invoice/:bookingId" element={lazyView(<Invoice />)} />
        <Route path="faq" element={lazyView(<FAQ />)} />
        <Route
          path="terms"
          element={lazyView(<Terms />)}
          handle={{ title: i18n.t('views.terms.title') }}
        />
        <Route
          path="privacy"
          element={lazyView(<Privacy />)}
          handle={{ title: i18n.t('views.privacy.title') }}
        />
        <Route
          path="law"
          element={lazyView(<Law />)}
          handle={{ title: i18n.t('law.title') }}
        />
        <Route
          path="login"
          element={lazyView(<Login />)}
          handle={{ title: i18n.t('share.login.title') }}
        />
        <Route
          path="reset-password"
          element={lazyView(<ResetPassword />)}
          handle={{ title: i18n.t('share.password.resetPassword') }}
        />
        <Route
          path="email-verification"
          element={lazyView(<EmailVerification />)}
          handle={{
            title: i18n.t('share.emailVerification.title')
          }}
        />
        <Route path="*" element={lazyView(<NotFound />)} />
      </Route>
      <Route path="/" element={<RedirectWithQuery />} />
    </Route>
  )
);

export default router;
